import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { datadogRum } from "@datadog/browser-rum";
import { renderToStaticMarkup } from "@hot-loader/react-dom/server";
import getMiliSecondDuration from "analytics/helper";
import parse from "html-react-parser";
import { Card, Container, Dimmer, Header, Icon } from "semantic-ui-react";

import { SetClientWorkflow } from "data/hub/clientWorkflows/actions";
import { AddComment } from "data/libs/comments";
import publishUpdate from "data/messages/actions";

import { SetAlert } from "components/Alerts/actions";
import { Loading } from "components/Loading";
import { PrivacyModeContext } from "components/PrivacyMode/Context";
import { RichTextEditor } from "components/RichTextEditor";
import { SetCommentTaskId } from "components/Task/actions";
import Tooltip from "components/Tooltip";

import Autosaver from "./CommentDraft/components/Autosaver";
import { renderMarkup } from "../utils/parsing";
import DeleteCommentAssociationButton from "./DeleteCommentAssociation";
import LinkedEntity from "./LinkedEntity";

class NewComment extends React.Component {
  static contextType = PrivacyModeContext;

  static defaultProps = {
    refetchComments: () => {},
    disabled: false,
  };

  static propTypes = {
    advisor: PropTypes.object.isRequired,
    selectedHub: PropTypes.object.isRequired,
    client: PropTypes.object.isRequired,
    clientWorkflowId: PropTypes.string.isRequired,
    clientWorkflows: PropTypes.object.isRequired,
    hub: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    reloadTaskBlock: PropTypes.func.isRequired,
    setAlert: PropTypes.func.isRequired,
    setCommentTaskId: PropTypes.func.isRequired,
    taskId: PropTypes.string.isRequired,
    refetchComments: PropTypes.func,
    setClientWorkflow: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    filterContext: PropTypes.shape({
      commentRequested: PropTypes.bool.isRequired,
      setActiveStandaloneDraft: PropTypes.func.isRequired,
      relatedEntityTitle: PropTypes.string,
      relatedEntityType: PropTypes.string,
      fulfillComment: PropTypes.func.isRequired,
      taskUuid: PropTypes.string,
      clientWorkflowUuid: PropTypes.string,
      clearAllFilters: PropTypes.func.isRequired,
    }).isRequired,
  };

  state = {
    body: "",
    adding: false,
    loading: false,
    addCommentTime: 0,
    draftGid: "",
  };

  modules = {
    clipboard: {
      matchVisual: false,
    },
  };

  handleBodyHTML = (content) => {
    this.setState({
      body: content,
    });
  };

  setAddingOpen = (open) => {
    this.setState({
      adding: open,
    });
  };

  removeTaskId = () => {
    const { setCommentTaskId } = this.props;
    setCommentTaskId("");
  };

  updateTaskState = (comment) => {
    const { taskId, clientWorkflowId, clientWorkflows, setClientWorkflow } =
      this.props;
    const clientWorkflow = clientWorkflows[clientWorkflowId];
    if (clientWorkflow) {
      const workflowCopy = { ...clientWorkflow };

      const foundTaskIndex = workflowCopy.tasks.findIndex((task) => {
        return task.id === taskId;
      });
      if (foundTaskIndex < 0) {
        console.warn("EditTaskDetails, Task ID ", taskId, " does not exist");
        return;
      }
      workflowCopy.tasks[foundTaskIndex].comments.push(comment);
      setClientWorkflow(workflowCopy);
    }
  };

  reloadTaskBlockIfNeeded = (taskUuid, clientWorkflowUuid) => {
    if (taskUuid || clientWorkflowUuid) {
      const { reloadTaskBlock } = this.props;
      // we have active filter applied
      // reload task block to get associated comment icon to the correct state
      reloadTaskBlock();
    }
  };

  addComment = (publishAction) => {
    this.setState({
      addCommentTime: new Date().getTime(),
    });
    const {
      advisor,
      client,
      filterContext,
      setAlert,
      taskId,
      selectedHub,
      refetchComments,
    } = this.props;
    const { fulfillComment, taskUuid, clientWorkflowUuid } = filterContext;
    const { body, draftGid } = this.state;

    // parsing to be replaced by renderMarkup
    // Include the advisor name in comments because they don't show up otherwise
    const isRedtail =
      advisor.integrations.find((i) => {
        return i.hubId === selectedHub.id;
      })?.type === "redtail";
    const commentHeader = isRedtail
      ? `<b>${advisor.firstName && advisor.firstName} ${
          advisor.lastName && advisor.lastName
        } Wrote:</b><br/><br/>`
      : "";
    const commentBody = commentHeader + body;

    const parsedBody = parse(commentBody, {
      replace: (domNode) => {
        // Add // to links without http or https at start
        if (domNode.attribs && domNode.attribs.href) {
          let url = domNode.attribs.href;
          if (
            !domNode.attribs.href.startsWith("http://") &&
            !domNode.attribs.href.startsWith("https://") &&
            !domNode.attribs.href.startsWith("//") &&
            !domNode.attribs.href.startsWith("mailto:")
          ) {
            url = `//${domNode.attribs.href}`;
          }
          return React.createElement(
            "a",
            { href: url, target: "_blank" },
            domNode.children.length > 0 ? domNode.children[0].data : url
          );
        }
        return domNode;
      },
    });

    const clientExternalId = (client.externalId || "").toString();
    this.setState({ loading: true });
    const request = {
      creator: advisor.externalId,
      content: renderToStaticMarkup(parsedBody),
      clientExternalId: clientExternalId,
      taskId: taskId,
      clientId: client.id,
      draftGid: draftGid,
    };

    let performCreateComment = () => {
      return AddComment(request);
    };
    // if we have an optional new mutation provided - use it instead, non-legacy mode
    if (publishAction) {
      performCreateComment = () => {
        return publishAction(renderMarkup(body, advisor, selectedHub, true));
      };
    }

    return performCreateComment()
      .then((response) => {
        this.updateTaskState(response);
        this.reloadTaskBlockIfNeeded(taskUuid, clientWorkflowUuid);
        this.setAddingOpen(false);
        fulfillComment();
        this.removeTaskId(); // reset association so following comments aren't linked to the currently selected entity
        this.setState({ body: "" });
        return refetchComments();
      })
      .catch(() => {
        setAlert({ type: "error", text: "Failed to add comment" });
      })
      .finally(() => {
        datadogRum.addAction("hubly_timespent_newcomment_add_comment", {
          duration: getMiliSecondDuration(this.state.addCommentTime),
        });
        this.setState({
          loading: false,
          addCommentTime: 0,
        });
      });
  };

  handleCommentAssociationRemoval = () => {
    const {
      filterContext: { clearAllFilters },
    } = this.props;

    this.setAddingOpen(true);
    this.removeTaskId();
    clearAllFilters();
  };

  render() {
    const { piiMask } = this.context;
    const { body, adding, loading, draftGid } = this.state;
    const {
      client,
      clientWorkflowId,
      clientWorkflows,
      taskId,
      disabled,
      filterContext,
      refetchComments,
    } = this.props;
    const {
      commentRequested,
      fulfillComment,
      relatedEntityTitle,
      relatedEntityType,
      setActiveStandaloneDraft,
      taskUuid,
      clientWorkflowUuid,
    } = filterContext;

    // old task matching mechanism; to be removed once comment-filter flag is fully rolled out
    let foundTask = {};
    if (taskId && clientWorkflowId) {
      const foundClientWorkflow = clientWorkflows[clientWorkflowId];
      if (foundClientWorkflow) {
        foundTask = clientWorkflows[clientWorkflowId].tasks.find((task) => {
          return task.id === taskId;
        });
      }
    }
    const relatedTitle = relatedEntityTitle || foundTask?.title;

    return (
      <React.Fragment>
        {!disabled && (adding || commentRequested) ? (
          <div style={{ marginLeft: "3em", marginTop: "1em" }}>
            <Header as="h5" style={{ marginBottom: "0.25em" }}>
              Add New Comment
            </Header>
            <RichTextEditor
              autoFocus
              className={piiMask("fs-block dd-privacy-mask")}
              data-test="comment-input"
              defaultValue={body}
              onChange={(content) => {
                this.handleBodyHTML(content);
              }}
              style={{ marginBottom: "0.5em" }}
              readOnly={loading}
            />
            <LinkedEntity title={relatedTitle} entityType={relatedEntityType}>
              <DeleteCommentAssociationButton
                disabled={loading}
                onLegacyDelete={this.handleCommentAssociationRemoval}
                onDelete={this.handleCommentAssociationRemoval}
              />
            </LinkedEntity>
            <Autosaver
              // no comment
              content={body}
              // no existing draft
              commentsLoading={loading}
              onPublish={(publishAction) => {
                return this.addComment(publishAction);
              }}
              onSave={async (saveResult) => {
                if (saveResult?.data?.draftGid) {
                  this.updateTaskState(saveResult);
                  this.reloadTaskBlockIfNeeded(taskUuid, clientWorkflowUuid);
                }
              }}
              onClose={() => {
                this.setAddingOpen(false);
                this.removeTaskId(); // clear task association in old state. new filters do NOT clear on close
                fulfillComment();
                this.setState({ body: "" });
                return refetchComments();
              }}
              draftGid={draftGid}
              setDraftGid={(newDraftGid) => {
                this.setState({ draftGid: newDraftGid });
                setActiveStandaloneDraft(newDraftGid);
              }}
              // taskId is coming from an old redux-based mechanism to store comment-task association
              // if that is not set, we should also check the taskUuid from the new filter context
              // old taskId state would be retired eventually. normally only one of them would be set
              taskUuid={taskId || taskUuid}
              clientWorkflowUuid={clientWorkflowUuid}
              setLoading={(desiredState = true) => {
                this.setState({ loading: desiredState });
              }}
              clientId={client.id}
              createNewComment
            />
          </div>
        ) : (
          <div style={{ float: "right" }}>
            <Tooltip
              hidden={!disabled}
              title="Comments are currently disabled because this contact was removed from your CRM. Please merge the contact."
              position="right"
            >
              <span>
                <Icon
                  data-test="add-comment-icon"
                  className="grey_on_hover_circle"
                  link
                  disabled={disabled}
                  name="plus"
                  onClick={() => {
                    if (!disabled) {
                      this.setAddingOpen(true);
                      this.setState({ draftGid: "" });
                    }
                  }}
                  title="Add Comment"
                />
              </span>
            </Tooltip>
          </div>
        )}
        {loading && (
          <Dimmer.Dimmable as={Container} dimmed>
            <Loading active messages={[]} inverted />
            <Container style={{ padding: "1em 0em 1em 3em" }}>
              <Card style={{ marginTop: "1em" }}>
                <Card.Content>
                  <Card.Header>Processing...</Card.Header>
                </Card.Content>
              </Card>
            </Container>
          </Dimmer.Dimmable>
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    advisor: state.hubly.data.advisor,
    client: state.hubly.data.hub.clients.activeClients[ownProps.clientId],
    clientWorkflowId: state.hubly.task.clientWorkflowId,
    clientWorkflows: state.hubly.data.hub.clientWorkflows,
    taskId: state.hubly.task.selectedTaskIdForComment,
    selectedHub: state.hubly.data.hub.selected.hub,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setAlert: (alert) => {
      dispatch(SetAlert(alert));
    },
    reloadTaskBlock: () => {
      dispatch(publishUpdate("CLIENT_CARD_TASKS", [true]));
    },
    setCommentTaskId: (taskId) => {
      dispatch(SetCommentTaskId(taskId));
    },
    setClientWorkflow: (workflows) => {
      dispatch(SetClientWorkflow(workflows));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(NewComment);
